import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { unitsMessages } from 'src/features/units/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { useGetServicePlanItem } from 'src/features/servicePlanItems/api/useGetServicePlanItem';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { commonMessages, generalMessages } from 'src/utils/messages';
import UpdateServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/UpdateServicePlanItemFormModal';
import DeleteServicePlanItemFormModal from './forms/DeleteServicePlanFormItemModal';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { useBillableMetricMap } from 'src/features/billableMetrics/hooks/useBillableMetricMap';
import ServicePlanItemDashboardTables from 'src/features/servicePlanItems/components/ServicePlanItemDashboardTables';
import { useGetServicePlan } from 'src/features/servicePlans/api/useGetServicePlan';
import DeactivateServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/DeactivateServicePlanItemFormModal';
import ActivateServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/ActivateServicePlanItemFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const ServicePlanItemDashboard = () => {
    const { formatMessage } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(servicePlanItemsMessages.servicePlanItemDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: servicePlanItem } = useGetServicePlanItem({
        id: servicePlanItemId,
    });
    const servicePlanId = servicePlanItem?.service_plan;

    const { data: servicePlan } = useGetServicePlan({
        id: servicePlanId ?? '',
        config: { enabled: !!servicePlanId },
    });

    const isServicePlanActive = Boolean(servicePlan?.is_active);

    // `billable_metric` is a bare UUID on the wire - the name has to come from the lookup.
    const metricMap = useBillableMetricMap();
    const billableMetric = servicePlanItem?.billable_metric
        ? metricMap.get(servicePlanItem.billable_metric)
        : undefined;

    const isActiveAsString = servicePlanItem?.is_active ? 'true' : 'false';
    const isRequiredAsString = servicePlanItem?.is_required ? 'true' : 'false';

    const dashboardBlockData = {
        section1: [
            {
                title: servicePlansMessages.code,
                value: servicePlanItem?.code ?? '-',
            },
            {
                title: servicePlanItemsMessages.externalId,
                value: servicePlanItem?.external_item_id ?? '-',
            },
            {
                title: servicePlanItemsMessages.metric,
                value: billableMetric ? `${billableMetric.name} (${billableMetric.code})` : '-',
            },
            {
                title: servicePlanItemsMessages.startingQuantity,
                value: servicePlanItem?.starting_quantity ?? '-',
            },
            {
                title: unitsMessages.unit,
                value: servicePlanItem?.unit
                    ? `${servicePlanItem.unit.name} (${servicePlanItem.unit.abbreviation})`
                    : '-',
            },
            {
                title: servicePlansMessages.isActive,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveAsString].label)}
                    </Badge>
                ),
            },
            {
                title: servicePlanItemsMessages.isRequired,
                value: (
                    <Badge theme={booleanBadgeOptions[isRequiredAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isRequiredAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isRequiredAsString].label)}
                    </Badge>
                ),
            },
        ],
    };

    return (
        <>
            {/*
              * A direct link to the plan, not navigate(-1): the item is reachable from the plan
              * dashboard, a search, a charge or a browser bookmark, and history sends the reader
              * wherever they happened to come from instead of where the label promises.
              */}
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to={servicePlanId ? `/service-plans/${servicePlanId}` : '/service-plans'}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(servicePlanItemsMessages.backToServicePlanDashboard)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {servicePlanItem?.name}
                        </SectionBlockTitle>
                        {permissionManageServicePlans && servicePlanItem && (
                            <div className="flex flex-row gap-2">
                                {servicePlanItem?.is_active && (
                                    <DeactivateServicePlanItemFormModal
                                        selectedServicePlanItem={servicePlanItem}
                                        semantics={{
                                            entryName: formatMessage(servicePlanItemsMessages.item).toLowerCase(),
                                        }}
                                    >
                                        <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                            <FontAwesomeIcon icon={ICON_MAP.TOGGLE_OFF} />
                                            <span className="hidden md:block">
                                                {formatMessage(servicePlansMessages.deactivate)}
                                            </span>
                                        </Button>
                                    </DeactivateServicePlanItemFormModal>
                                )}
                                {!servicePlanItem?.is_active && (
                                    <ActivateServicePlanItemFormModal
                                        selectedServicePlanItem={servicePlanItem}
                                        semantics={{
                                            entryName: formatMessage(servicePlanItemsMessages.item).toLowerCase(),
                                        }}
                                    >
                                        <Button size="mdIcon" variant="default" className="cursor-pointer">
                                            <FontAwesomeIcon icon={ICON_MAP.TOGGLE_ON} />
                                            <span className="hidden md:block">
                                                {formatMessage(servicePlansMessages.activate)}
                                            </span>
                                        </Button>
                                    </ActivateServicePlanItemFormModal>
                                )}

                                {!isServicePlanActive && (
                                    <>
                                        <UpdateServicePlanItemFormModal
                                            selectedServicePlanItem={servicePlanItem}
                                            semantics={{
                                                entryName: formatMessage(servicePlanItemsMessages.item).toLowerCase(),
                                            }}
                                        >
                                            <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                                <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                                <span className="hidden md:block">
                                                    {formatMessage(commonMessages.edit)}
                                                </span>
                                            </Button>
                                        </UpdateServicePlanItemFormModal>
                                        <DeleteServicePlanItemFormModal
                                            selectedServicePlanItem={servicePlanItem}
                                            semantics={{
                                                entryName: formatMessage(servicePlanItemsMessages.item).toLowerCase(),
                                            }}
                                        >
                                            <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                                <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                                <span className="hidden md:block">
                                                    {formatMessage(generalMessages.delete)}
                                                </span>
                                            </Button>
                                        </DeleteServicePlanItemFormModal>
                                    </>
                                )}
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={0} />
                </SectionBlock>

                <ServicePlanItemDashboardTables isServicePlanItemActive={Boolean(servicePlanItem?.is_active)} />
            </div>
        </>
    );
};

export default ServicePlanItemDashboard;
